/* eslint-disable import/no-unused-modules */
import React, { useState, useEffect, useMemo } from 'react';
import {
    loadStripe,
    type Stripe,
    type StripeElements,
    type StripeElementsOptions,
} from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@rocco/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import { LoaderCircle, X } from 'lucide-react';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import { proposalFormatPrice } from '../../util/utils';
import type { Currency } from '../../type/types';

/** Stripe error shape returned by confirmSetup / confirmPayment */
export interface StripeError {
    message?: string;
    type?: string;
    code?: string;
}

/** Stripe Elements options with typed appearance config */
export interface ElementsOptions extends StripeElementsOptions {
    clientSecret: string;
    appearance: {
        theme: 'stripe';
        variables: {
            colorPrimary: string;
            colorBackground: string;
            colorText: string;
            colorDanger: string;
            fontFamily: string;
            spacingUnit: string;
            borderRadius: string;
        };
    };
}

/** Data needed to display the payment summary in the popup */
export interface PaymentDisplayData {
    amount: number;
    currency: string;
    amountWithSurcharge: number;
    surchargeConfig: { rate: number };
}

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

/** Default Stripe Elements appearance used across all payment popups */
const STRIPE_APPEARANCE = {
    theme: 'stripe' as const,
    variables: {
        colorPrimary: '#0570de',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: 'Ideal Sans, system-ui, sans-serif',
        spacingUnit: '2px',
        borderRadius: '4px',
    },
};

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

/**
 * Build Stripe Elements options from a client secret.
 * Returns `undefined` when no secret is available yet.
 */
export const buildElementsOptions = (
    clientSecret: string | null
): ElementsOptions | undefined =>
    clientSecret ? { clientSecret, appearance: STRIPE_APPEARANCE } : undefined;

// ---------------------------------------------------------------------------
// Shared hooks
// ---------------------------------------------------------------------------

/**
 * Create a memoised Stripe.js promise using the publishable key from the
 * invoice config (or the global fallback) and the connected account id.
 */
export const useStripeInstance = (
    stripeAccount: string | null,
    publishableKey: string | null
) => {
    const config = useConfigContext();

    return useMemo(() => {
        // Use publishable key from invoice's Stripe config, fallback to global config
        const key = publishableKey || config.stripeEcommerce.publishableKey;

        // Create Stripe instance with stripeAccount for Stripe Connect functionality
        return loadStripe(key, {
            stripeAccount: stripeAccount || undefined,
        });
    }, [stripeAccount, publishableKey, config.stripeEcommerce.publishableKey]);
};

/**
 * Close the popup when the browser back button is pressed on mobile.
 * Pushes a dummy history state when the popup opens and pops it on close.
 */
export const useBackButtonClose = (
    isOpen: boolean,
    closePopup: () => void,
    stateKey: string
) => {
    useEffect(() => {
        if (isOpen) {
            // Push a dummy state to the history when dialog opens
            window.history.pushState({ [stateKey]: true }, '');

            const handlePopState = (_event: PopStateEvent) => {
                closePopup();
            };

            window.addEventListener('popstate', handlePopState);

            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        } else if (window.history.state?.[stateKey]) {
            // Remove the dummy state only when the popup is closed
            window.history.back();
        }
    }, [isOpen, closePopup, stateKey]);
};

// ---------------------------------------------------------------------------
// Shared form component
// ---------------------------------------------------------------------------

export interface StripePaymentFormProps {
    /** Stripe confirm callback – receives the Stripe & Elements instances */
    confirmAction: (
        stripe: Stripe,
        elements: StripeElements
    ) => Promise<{ error?: StripeError }>;
    /** Called after a successful confirmation */
    onSuccess?: () => void;
    /** Called when an error occurs */
    onError?: (error: string) => void;
    /** Invoice payment id used to fire the processing webhook */
    invoicePaymentId?: string | null;
    /** Close the parent popup */
    closePopup: () => void;
    /** Message displayed on success (e.g. "Payment successful!") */
    successMessage: string;
    /** Fallback message when Stripe returns an error without a message */
    failureMessage: string;
    /** HTML id for the form element */
    formId: string;
}

/**
 * Generic Stripe payment form used by both setup-intent and payment-intent
 * popups. The caller provides the specific `confirmAction` callback.
 */
export const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
    confirmAction,
    onSuccess,
    onError,
    invoicePaymentId,
    closePopup,
    successMessage,
    failureMessage,
    formId,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements || !isPaymentElementReady) {
            return;
        }

        setIsProcessing(true);
        setMessage(null);

        try {
            const { error } = await confirmAction(stripe, elements);

            if (error) {
                // Only display server-side errors (card_error, api_error, etc.)
                // Stripe's PaymentElement handles inline validation errors
                // Error types: https://docs.stripe.com/error-handling
                const shouldDisplayError = error.type !== 'validation_error';

                if (shouldDisplayError) {
                    const errorMsg = error.message || failureMessage;
                    setMessage(errorMsg);
                    onError?.(errorMsg);
                } else {
                    setMessage('');
                    onError?.(error.message || 'Validation error');
                }
            } else {
                // Fire-and-forget: mark invoice payment as processing
                if (invoicePaymentId) {
                    const formData = new FormData();
                    formData.append('invoicePaymentId', invoicePaymentId);

                    fetch('/remix-api/invoice-payment-set-processing-payment', {
                        method: 'POST',
                        body: formData,
                    }).catch(() => {
                        // Silently ignore – backend will continue processing
                    });
                }

                setMessage(successMessage);
                onSuccess?.();
            }
        } catch (err) {
            const errorMsg =
                err instanceof Error
                    ? err.message
                    : 'An unexpected error occurred';
            setMessage(errorMsg);
            onError?.(errorMsg);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6" id={formId}>
            <PaymentElement
                onReady={() => setIsPaymentElementReady(true)}
                options={{ layout: 'tabs' }}
            />
            <img
                src="/assets/website/images/stripe/guaranteed-safe-stripe.png"
                className="h-auto w-auto"
                alt="Powered by Stripe"
            />
            {message && (
                <div
                    className={`rounded-md p-3 text-sm ${
                        message.includes('successful')
                            ? 'bg-green-50 text-green-700 border-green-200 border'
                            : 'border border-red-200 bg-red-50 text-red-700'
                    }`}
                >
                    {message}
                </div>
            )}
            <div className="flex flex-row gap-3 pb-8 md:pb-0">
                <Button
                    type="submit"
                    disabled={
                        !stripe ||
                        !elements ||
                        !isPaymentElementReady ||
                        isProcessing
                    }
                    className="flex-1"
                >
                    {isProcessing ? (
                        <>
                            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                            Processing...
                        </>
                    ) : (
                        'Pay Now'
                    )}
                </Button>
                <Button
                    type="button"
                    color="white"
                    onClick={closePopup}
                    disabled={isProcessing}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
};

// ---------------------------------------------------------------------------
// Shared dialog wrapper
// ---------------------------------------------------------------------------

export interface StripePopupDialogProps {
    /** Whether the popup is open */
    isOpen: boolean;
    /** Close handler */
    closePopup: () => void;
    /** Optional payment summary data */
    paymentData?: PaymentDisplayData | null;
    /** Show a loading spinner instead of the form */
    isLoading: boolean;
    /** Loading spinner label */
    loadingMessage?: string;
    /** Error message to display (hides form when set) */
    errorMessage?: string | null;
    /** Stripe.js promise from useStripeInstance */
    stripePromise: Promise<Stripe | null>;
    /** Stripe Elements options (includes clientSecret + appearance) */
    options?: ElementsOptions;
    /** Connected Stripe account id – Elements are only rendered when set */
    stripeAccount?: string | null;
    /** The form to render inside the Elements provider */
    children: React.ReactNode;
}

/**
 * Shared popup dialog chrome for Stripe payment / setup-intent flows.
 * Renders the header, payment summary, loading / error states,
 * and wraps children in a Stripe Elements provider when ready.
 */
export const StripePopupDialog: React.FC<StripePopupDialogProps> = ({
    isOpen,
    closePopup,
    paymentData,
    isLoading,
    loadingMessage = 'Processing payment details...',
    errorMessage,
    stripePromise,
    options,
    stripeAccount,
    children,
}) => {
    return (
        <Dialog open={isOpen}>
            <DialogContent
                closeButton={false}
                className="max-h-[100vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                        <span>Complete Payment</span>
                        <Button
                            color="white"
                            size="compact"
                            onClick={closePopup}
                            className="h-6 w-6 p-0"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </DialogTitle>
                    <DialogDescription>
                        Enter your payment details to securely complete your
                        purchase.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {paymentData && (
                        <div className="text-center">
                            <p className="text-sm text-gray-600">Amount Due</p>
                            <p className="text-2xl font-semibold">
                                {proposalFormatPrice(
                                    paymentData.amountWithSurcharge,
                                    paymentData.currency as Currency
                                )}
                            </p>
                            {paymentData.surchargeConfig.rate > 0 && (
                                // Credit/Debit (2.7% fee)
                                // Subtotal: 1030.07
                                // Total: 1057.88 NZD
                                <>
                                    <p className="mt-1 text-sm text-gray-600">
                                        {`Credit/Debit (${paymentData.surchargeConfig.rate * 100}% fee)`}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-600">
                                        {'Subtotal: '}
                                        {proposalFormatPrice(
                                            paymentData.amount,
                                            paymentData.currency as Currency
                                        )}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-600">
                                        {'Total: '}
                                        {proposalFormatPrice(
                                            paymentData.amountWithSurcharge,
                                            paymentData.currency as Currency
                                        )}
                                    </p>
                                </>
                            )}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <LoaderCircle className="h-6 w-6 animate-spin" />
                            <span className="ml-2 text-sm text-gray-600">
                                {loadingMessage}
                            </span>
                        </div>
                    ) : errorMessage ? (
                        <div className="py-4">
                            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                {errorMessage}
                            </div>
                            <div className="mt-4 flex justify-end">
                                <Button color="white" onClick={closePopup}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    ) : (
                        !(options && stripeAccount) && (
                            <div className="flex items-center justify-center py-8">
                                <LoaderCircle className="h-6 w-6 animate-spin" />
                                <span className="ml-2 text-sm text-gray-600">
                                    Initializing payment...
                                </span>
                            </div>
                        )
                    )}
                    {options && stripeAccount && (
                        <Elements stripe={stripePromise} options={options}>
                            {children}
                        </Elements>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
